/**
 * Согласование существительного с числом в русских подписях интерфейса —
 * порт backend `RussianPlural` (service/impl/RussianPlural.java), чтобы web и
 * сервер склоняли одинаково: «1 вещь», «3 вещи», «5 вещей», «21 вещь», «11 вещей».
 *
 * Правило стандартное: 1 — единственное, 2–4 — форма родительного единственного,
 * 0 и 5–20 — родительный множественного, дальше по последним двум цифрам.
 */
export function pluralForm(count: number, one: string, few: string, many: string): string {
    const abs = Math.abs(Math.trunc(count));
    const lastTwo = abs % 100;
    if (lastTwo >= 11 && lastTwo <= 14) return many;
    const last = abs % 10;
    if (last === 1) return one;
    if (last >= 2 && last <= 4) return few;
    return many;
}

/** «5 образов» / «1 образ» — число вместе с согласованным словом. */
export function plural(count: number, one: string, few: string, many: string): string {
    return `${count} ${pluralForm(count, one, few, many)}`;
}

/** «1 вещь / 2 вещи / 5 вещей». */
export const pluralItems = (count: number): string => plural(count, 'вещь', 'вещи', 'вещей');

/** «1 раз / 2 раза / 5 раз». */
export const pluralTimes = (count: number): string => plural(count, 'раз', 'раза', 'раз');

/** «1 носка / 2 носки / 5 носок». */
export const pluralWears = (count: number): string => plural(count, 'носка', 'носки', 'носок');

/** «1 образ / 2 образа / 5 образов». */
export const pluralOutfits = (count: number): string => plural(count, 'образ', 'образа', 'образов');

/**
 * «1 вещи / 2 вещей / 5 вещей» — родительный падеж для оборотов «у…», «для…», «из…», «без…».
 * Отдельная функция, потому что {@link pluralItems} даёт именительный («1 вещь») и в таких
 * оборотах читается неверно: «У 1 вещь не указана цена».
 */
export const pluralItemsOf = (count: number): string =>
    `${count} ${pluralForm(count, 'вещи', 'вещей', 'вещей')}`;

/** «1 день / 2 дня / 5 дней». */
export const pluralDays = (count: number): string => plural(count, 'день', 'дня', 'дней');

/** «1 группа / 2 группы / 5 групп». */
export const pluralGroups = (count: number): string => plural(count, 'группа', 'группы', 'групп');

/** «1 список / 2 списка / 5 списков». */
export const pluralLists = (count: number): string => plural(count, 'список', 'списка', 'списков');

/** «1 стирка / 2 стирки / 5 стирок». */
export const pluralWashes = (count: number): string => plural(count, 'стирка', 'стирки', 'стирок');
